import { PureComponent } from 'react';
import { layer, source, feature } from 'react-openlayers';
import type { Coordinate } from 'ol/coordinate';

import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { MAP_EVENTS } from '../../../mapEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { convertProjection4329 } from '../../../utils';

const Z_INDEX_ROUTING_BOUNDARY = -1;

interface RoutingBoundaryLayerProps {
  visible: boolean;
}

/** Each polygon = outer ring + zero or more holes. */
type RenderPolygon = Coordinate[][];

interface RoutingBoundaryLayerState {
  polygons: RenderPolygon[] | null;
}

/**
 * Renders the "safe routing area" — boundary buffered inward + unsafe zones
 * expanded outward. Computed by `Mission.getSafeRoutingBoundary` (cached);
 * this layer just reads the result on mission/zone changes. The result may be
 * a Polygon (single connected piece) or a MultiPolygon (disjoint safe areas
 * remain after buffering / unsafe-zone subtraction); both are rendered.
 *
 * Subscribes to:
 *   - MISSION_EVENTS.CREATED_OR_DELETED (mission swap)
 *   - MISSION_EVENTS.BOUNDARY_UPDATED   (field boundary edits)
 *   - MAP_EVENTS.ZONES_UPDATED          (unsafe / pullin / slow zone edits)
 */
export default class RoutingBoundaryLayer extends PureComponent<RoutingBoundaryLayerProps, RoutingBoundaryLayerState> {
  constructor(props: RoutingBoundaryLayerProps) {
    super(props);
    this.state = { polygons: null };
    this.rebuild = this.rebuild.bind(this);
  }

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuild);
    EventBus.on(MISSION_EVENTS.BOUNDARY_UPDATED, this.rebuild);
    EventBus.on(MAP_EVENTS.ZONES_UPDATED, this.rebuild);
    this.rebuild();
  }

  componentDidUpdate(prevProps: RoutingBoundaryLayerProps) {
    if (prevProps.visible !== this.props.visible) {
      this.rebuild();
    }
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuild);
    EventBus.remove(MISSION_EVENTS.BOUNDARY_UPDATED, this.rebuild);
    EventBus.remove(MAP_EVENTS.ZONES_UPDATED, this.rebuild);
  }

  rebuild() {
    if (!this.props.visible) {
      this.setState({ polygons: null });

      return;
    }

    const mission = getCurrentMission();

    if (!mission) {
      this.setState({ polygons: null });

      return;
    }

    const boundary = mission.getSafeRoutingBoundary();

    if (!boundary) {
      this.setState({ polygons: null });

      return;
    }

    const project = (poly: Coordinate[][]): RenderPolygon =>
      poly.map((ring) => ring.map((coord) => convertProjection4329(coord)));

    const polygons: RenderPolygon[] =
      boundary.geometry.type === 'Polygon'
        ? [project(boundary.geometry.coordinates)]
        : boundary.geometry.coordinates.map(project);

    this.setState({ polygons });
  }

  render() {
    if (!this.state.polygons) {
      return null;
    }

    return (
      // @ts-ignore - layer.Vector children typing
      <layer.Vector zIndex={Z_INDEX_ROUTING_BOUNDARY}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>
          {this.state.polygons.map((rings, i) => (
            <feature.PolygonReact
              key={`routing-boundary-${i}`}
              coordinates={rings}
              strokeOptions={{ color: [180, 0, 255, 1], width: 3, lineDash: [8, 6] }}
            />
          ))}
        </source.VectorSourceReact>
      </layer.Vector>
    );
  }
}
